import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthLayout from '../../components/auth/AuthLayout';
import { useAuth } from '../../contexts/AuthContext';
import authServices from '../../services/AuthServices';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved mobile number on mount
  useEffect(() => {
    const savedMobile = localStorage.getItem('rememberedMobile');
    if (savedMobile) {
      setFormData(prev => ({ ...prev, username: savedMobile }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // For username field, allow digits only for demo
    if (name === 'username') {
      const formattedValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const username = formData.username.trim();
    const password = formData.password.trim();


    // Validation
    if (!username) {
      setError('Please enter mobile number');
      setLoading(false);
      return;
    }

    if (!password) {
      setError('Please enter your password');
      setLoading(false);
      return;
    }

    try {
      // Call the actual API
      const response = await authServices.login(username, password);
      
      if (response.success) {
        // Handle Remember Me
        if (rememberMe) {
          localStorage.setItem('rememberedMobile', username);
        } else {
          localStorage.removeItem('rememberedMobile');
        }

        // Login successful
        login(response.success);
        toast.success('Login successful!');
        
        // Navigate based on user role
        const userRole = response.success.user.role;
        if (userRole === 'admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else if (response.errorMsg) {
        setError(response.errorMsg);
      }
    } catch (error) {
      console.error('Login Error:', error);

      // Handle different error scenarios
      let errorMessage = 'An error occurred. Please try again.';

      if (error.message) {
        errorMessage = error.message;
      } else if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.status === 401) {
        errorMessage = 'Invalid username or password';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.status === 0 || !error.status) {
        errorMessage = 'Network error. Please check your internet connection.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back! Please sign in to your account."
    >
      {error && (
        <div className="toast-error">
          <div className="toast-content">
            <div className="toast-icon">⚠️</div>
            <div className="toast-text">{error}</div>
            <button
              className="toast-close"
              onClick={() => setError('')}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Mobile Number</label>
          <input
            type="text"
            name="username"
            className="form-input"
            placeholder="Enter mobile number"
            maxLength={10}
            value={formData.username}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Password</label>
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              className="form-input"
              placeholder="********"
              value={formData.password}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>
          <div className="password-options">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" className="forgot-password-link">Forgot password?</Link>
          </div>
        </div>

        <button type="submit" className="signin-btn" disabled={loading}>
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>

      <div className="signup-section">
        <p className="signup-text">
          Are you new?{' '}
          <a href="https://signupdms.webdekho.in/" target='_blank' rel="noreferrer" className="signup-link">
            Create an Account
          </a>
        </p>
      </div>

    </AuthLayout>
  );
};

export default Login;